import React, { FormEvent, useEffect, useState } from 'react';
import { SubContentProps } from '../../LanguageSetting';
import { CertificateOfEmploymentProps } from '../index';
import useCertificateRequestForm, {
  CertificateRequestForm,
} from './useCertificateRequestForm';
import {
  useCertifySmsCode,
  useGetCertifyCode,
  useHistoryEmpProof,
  usePrintEmpProof,
} from '@/hooks/apiHooks';
import { getPhoneNumberFormat } from '@/utils/format';
import { AxiosError } from 'axios';
import { useSnackbar } from '@/hooks/global-tools';
import { useTranslation } from 'react-i18next';

const useCertificateOfEmploymentProps = (
  settings: SubContentProps,
): CertificateOfEmploymentProps => {
  const { t } = useTranslation();
  const {
    myInfo: { phoneMobile, passportId, employeeNumber },
  } = settings;

  const { showSuccess, showError } = useSnackbar();

  const methods = useCertificateRequestForm();
  const {
    handleSubmit,
    setValue,
    getValues,
    reset,
    getFieldState,
    formState: { isValid, isDirty },
  } = methods;

  const {
    isLoading: isRequestHistoryLoading,
    data = [],
    mutate: updateList,
  } = useHistoryEmpProof({
    employeeNumber,
    listSize: 500,
    currentPage: 1,
  });

  const isCodeRequested = getValues('verificationId') !== '';
  const isVerified = getValues('verified') === true;
  const isFormValid = isVerified && isDirty && isValid;

  const codeOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(
      'verificationCode',
      e.target.value.trim().replace(/\D/g, '').slice(0, 6),
      { shouldValidate: true },
    );
  };

  const [isCodeRequestLoading, setIsCodeRequestLoading] = useState(false);
  const requestButtonOnClick = async () => {
    try {
      setIsCodeRequestLoading(true);

      const {
        data: { smsVerificationId },
      } = await useGetCertifyCode(phoneMobile);
      setValue('verificationId', smsVerificationId);
    } catch (e) {
      if (e instanceof AxiosError) {
        showError({
          message: e.response?.data?.message || t('인증 요청에 실패했습니다.'),
        });
      }
    } finally {
      setIsCodeRequestLoading(false);
    }
  };

  const [isVerifyLoading, setIsVerifyLoading] = useState(false);
  const verifyButtonOnClick = async () => {
    try {
      setIsVerifyLoading(true);

      await useCertifySmsCode({
        smsVerificationId: getValues('verificationId'),
        smsVerificationCode: getValues('verificationCode'),
      });
      setValue('verified', true);
    } catch (e) {
      if (e instanceof AxiosError) {
        const errorData = e.response?.data;

        let errorMessage = errorData?.message || t('인증에 실패했습니다.');
        if (errorData?.code === '0422') {
          errorMessage = t('인증번호가 일치하지 않습니다.');
        }

        showError({
          message: errorMessage,
        });
      }
    } finally {
      setIsVerifyLoading(false);
    }
  };

  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const handleFormOnSubmit = (e: FormEvent) => {
    e.preventDefault();

    handleSubmit(async (data: CertificateRequestForm) => {
      try {
        setIsPrintLoading(true);

        const res = await usePrintEmpProof({
          passportId,
          outputReason: data.purpose,
          smsVerificationId: data.verificationId,
          smsVerificationCode: data.verificationCode,
        });

        if (window.isRNWebview) {
          const sendData = JSON.stringify({
            actionType: 'OPEN_APP',
            url: res,
          });
          window?.ReactNativeWebView?.postMessage(sendData);
        } else {
          window.open(res, '_blank', 'noopener, noreferrer');
        }

        await updateList();
        reset({
          verificationCode: '',
          verificationId: '',
          verified: false,
          purpose: '',
          phoneNumber: getPhoneNumberFormat(phoneMobile),
        });
        showSuccess({ message: t('발급 신청이 완료되었습니다.') });
      } catch (e) {
        if (e instanceof AxiosError) {
          const errorData = e.response?.data;
          showError({
            message: errorData?.message || t('발급 신청에 실패했습니다.'),
          });
        } else {
          showError({ message: t('발급 신청에 실패했습니다.') });
        }
      } finally {
        setIsPrintLoading(false);
      }
    })();
  };

  useEffect(() => {
    if (phoneMobile) {
      setValue('phoneNumber', getPhoneNumberFormat(phoneMobile));
    }
  }, [phoneMobile]);

  return {
    formProps: {
      methods,
      onSubmit: handleFormOnSubmit,
      id: 'mypage-certificate-of-employment',
    },
    requestCodeButtonTitle: isCodeRequested ? t('재전송') : t('인증번호 전송'),
    requestButtonOnClick,
    isCodeRequested,
    isVerified,
    isRequestButtonDisabled: getFieldState('phoneNumber').invalid,
    isLoading: isCodeRequestLoading || isVerifyLoading || isPrintLoading,
    isCodeRequestLoading,
    isVerifyLoading,
    isPrintLoading,
    codeOnChange,
    codeHelperText: isVerified ? t('인증되었습니다.') : '',
    isVerifyButtonDisabled:
      getFieldState('phoneNumber').invalid ||
      getFieldState('verificationCode').invalid ||
      isVerified,
    isSubmitButtonDisabled: !isFormValid,
    verifyButtonOnClick,
    isRequestHistoryLoading,
    requestHistory: data.map((item) => ({
      id: item.cartNumber,
      requestDate: item.createDate.replaceAll('.', '-'),
      purpose: item.ouputReason ?? '-',
    })),
  };
};

export default useCertificateOfEmploymentProps;

export type EmpCertificateRequestData = {
  readonly id: number;
  readonly requestDate: string;
  readonly purpose: string;
};
